/**
 * ChildControlsTable — controls inherited from child TARAs.
 * Read-only data + Relevant checkbox + Justification input.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import CategoryPill from '../../../../components/ui/CategoryPill';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import { type ControlRecord } from './controlsFieldSchema';
import styles from './Step8Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const renderType = (row: ChildControlView) =>
  row.control_type ? <CategoryPill label={String(row.control_type)} size="sm" /> : '';

export interface ChildControlView extends ControlRecord {}

export interface ChildControlsTableProps {
  controls: ChildControlView[];
  onToggleRelevance: (controlId: string) => void;
  onJustificationChange: (controlId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildControlsTable({
  controls,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildControlsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return controls;
    return controls.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [controls, search]);

  const totalPages = Math.max(1, Math.ceil(searched.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const paged = useMemo(
    () => searched.slice((page - 1) * pageSize, page * pageSize),
    [searched, page, pageSize],
  );
  const keyed = useMemo(
    () => paged.map((r, i) => ({ ...r, _rowKey: `${r.control_id ?? 'row'}::${(page - 1) * pageSize + i}` })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'control_id', label: 'ID', sortable: true, width: '7%' },
      { key: 'control_name', label: 'Name', sortable: true, width: '17%' },
      { key: 'control_description', label: 'Description', sortable: false, width: '30%' },
      { key: 'control_type', label: 'Type', sortable: true, width: '11%', render: renderType },
      { key: 'goal_id', label: 'Parent Goal', sortable: true, width: '8%' },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '9%',
        render: (row: ChildControlView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.control_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.control_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '18%',
        render: (row: ChildControlView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.control_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  return (
    <DataTableAny
      columns={columns}
      data={keyed}
      rowKey="_rowKey"
      emptyMessage="No inherited controls from child TARAs."
      toolbar={
        <TableToolbarAny
          search={{
            value: search,
            onChange: (v: string) => { setSearch(v); setPage(1); },
            placeholder: 'Search inherited controls…',
          }}
          count={{ value: searched.length, label: 'Inherited Controls' }}
          showFilterButton={false}
          actions={
            <RelevanceEditToggle
              editing={liveEditing}
              onToggle={handleToggle}
              pending={saving}
              disabled={disabled}
            />
          }
        />
      }
      pagination={
        <TablePaginationAny
          currentPage={page}
          totalItems={searched.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onItemsPerPageChange={(size: number) => { setPageSize(size); setPage(1); }}
        />
      }
    />
  );
}
